import { Button } from '@heroui/react';
import { experience } from '@logto/schemas';
import type { TFuncKey } from 'i18next';
import { type To } from 'react-router-dom';

import ColumnFrame from '@/Layout/ColumnFrame';
import StaticPageLayout from '@/Layout/StaticPageLayout';
import ErrorScene from '@/components/illustrations/ErrorScene';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import DynamicT from '@/shared/components/DynamicT';
import NavBar from '@/shared/components/NavBar';
import PageMeta from '@/shared/components/PageMeta';

import SupportInfo from './SupportInfo';

type PrimaryAction = {
  readonly title: TFuncKey;
  readonly to?: To;
  readonly replace?: boolean;
  readonly onClick?: () => void;
};

type Props = {
  readonly title?: TFuncKey;
  readonly message?: TFuncKey;
  readonly rawMessage?: string;
  readonly isNavbarHidden?: boolean;
  readonly primaryAction?: PrimaryAction;
};

const ErrorPage = ({
  title = 'description.not_found',
  message,
  rawMessage,
  isNavbarHidden,
  primaryAction,
}: Props) => {
  const navigate = useNavigateWithPreservedSearchParams();
  const resolvedMessage =
    message ??
    (title === 'description.not_found' && !rawMessage
      ? 'description.not_found_description'
      : undefined);
  const hasMessage = Boolean(rawMessage ?? resolvedMessage);
  const canGoBack = history.length > 1;

  // Back can land on the route that just errored, so a forward escape is always offered.
  const resolvedAction: PrimaryAction = primaryAction ?? {
    title: 'description.back_to_sign_in',
    to: `/${experience.routes.signIn}`,
    replace: true,
  };

  return (
    <StaticPageLayout>
      <PageMeta titleKey={title} />
      {canGoBack && (
        <ColumnFrame slot="nav">
          <NavBar
            isHidden={isNavbarHidden}
            onBack={() => {
              navigate(-1);
            }}
          />
        </ColumnFrame>
      )}
      <div className="flex flex-1 flex-col items-center justify-center text-center lg:desktop:items-start lg:desktop:text-start">
        <ErrorScene />
        <h1
          tabIndex={-1}
          className="page-title mt-6 text-2xl/7 font-semibold tracking-tight text-balance text-foreground wrap-anywhere outline-none"
        >
          <DynamicT forKey={title} />
        </h1>
        {hasMessage && (
          <p className="page-description mt-2 text-sm text-balance whitespace-pre-wrap text-muted">
            {rawMessage ?? <DynamicT forKey={resolvedMessage} />}
          </p>
        )}
        <Button
          fullWidth
          size="lg"
          className="mt-6"
          onPress={() => {
            if (resolvedAction.onClick) {
              resolvedAction.onClick();
              return;
            }

            if (resolvedAction.to) {
              navigate(resolvedAction.to, { replace: resolvedAction.replace });
            }
          }}
        >
          <DynamicT forKey={resolvedAction.title} />
        </Button>
        <SupportInfo />
      </div>
    </StaticPageLayout>
  );
};

export default ErrorPage;
